import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    getUserSearchLimits,
    removeUserSearchLimit,
    saveUserSearchLimit,
} from 'src/openapi/admin/apis/UserSearchLimitsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {SaveContext} from 'src/hooks/use-common-data-hook';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

type Props = {
    userId: number,
};

export const useUserSearchLimits = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback((signal: AbortSignal) => apiDispatch(
        getUserSearchLimits({userId}, {signal}),
    ), [userId, apiDispatch]);

    const saveDispatchAction = useCallback(async (
        limits: Limit[],
        signal: AbortSignal,
        extra: (SaveOptions & {initialLimits?: Limit[]}) | undefined,
        {initialData, setInitialData}: SaveContext<Limit[]>,
    ) => {
        const {initialLimits, reason} = extra || {};
        if (!initialLimits) {
            return Promise.resolve({type: '', payload: {
                data: {message: 'No initial limits found'},
                success: false,
                error: true,
            } as ErrorPayload});
        }

        const removedLimits = initialData.filter(initialLimit => (
            !limits.find(limit => limit.limitId === initialLimit.limitId)
        ));

        const savedLimits = initialData.filter(initial => limits.some(limit => limit.limitId === initial.limitId));
        if (!isDeepEqual({prev: savedLimits, current: limits})) {
            const saveSearchLimitsResponse = await apiDispatch(saveUserSearchLimit({
                userId,
                userLimitsSaveRequest: {limits, reason},
            }, {signal}));
            if (saveSearchLimitsResponse.payload.error) {
                return Promise.resolve({type: '', payload: saveSearchLimitsResponse.payload});
            }
            setInitialData([...limits, ...removedLimits]);
        }

        if (removedLimits.length) {
            const removeSearchLimitsResponse = await apiDispatch(removeUserSearchLimit({
                userId,
                userLimitsRemoveRequest: {limitIds: removedLimits.map(({limitId}) => limitId), reason},
            }, {signal}));
            if (removeSearchLimitsResponse.payload.error) {
                return Promise.resolve({type: '', payload: removeSearchLimitsResponse.payload});
            }
        }

        return Promise.resolve({type: '', payload: {
            data: undefined,
            success: true,
            error: false,
        } as SuccessPayload<void>});
    }, [userId, apiDispatch]);

    const hook = useCommonDataHook({
        initValue: [],
        getDispatchAction,
        saveDispatchAction,
    });
    const {saveChanges: save} = hook;
    const saveChanges = useCallback((initialLimits?: Limit[], options?: SaveOptions) => (
        save({initialLimits, ...options})
    ), [save]);

    return {...hook, saveChanges};
};
